<template>
  <div>
    <va-modal
      :value="showKioskModal"
      ref="createOrUpdateKiosk"
      :okText="$t('modals.buttons.save')"
      :okClass="'btn btn-primary btn-micro'"
      :okToClose="false"
      @ok="submit"
      :cancelText="$t('modals.buttons.back')"
      :cancelClass="'btn-cancelar btn btn-primary btn-micro'"
      @cancel="close"
      size="large"
      :ok-disabled="submitDisabled">

      <div slot="header" class="modal-title">
        <h4 class="mb-0">
          <b>{{ isUpdate ? kiosk.name : $t('kiosks.creating') }}</b>
        </h4>
      </div>

      <div v-if="isLoading" class="my-4 py-4 d-flex flex-column justify-content-center align-items-center">
        <h6><b>{{$t('kiosks.is_loading')}}</b></h6>
        <LoadingAnimation :size="'5px'" class="d-flex justify-content-center pt-4 pb-4"></LoadingAnimation>
      </div>

      <div v-else class="evollu-form">

        <div class="row">

          <div class="col-12 col-md-8">
            <label class="form-label">{{$t('kiosks.name')}}</label>
            <va-input
              id="facility-code-input"
              v-model="form.data.name"
              maxLength="255"
              :placeholder="$t('kiosks.name')"
              :disabled="!kiosk.active_license && !isFromEvollu"
              :error="form.errors.has('name')"
              :error-messages="[$t(form.errors.get('name'))]"
              @focus="form.errors.clear('name')"
            />
          </div>

          <div class="col-12 col-md-4">
            <label class="form-label">{{$t('kiosks.version')}}</label>
            <va-input
              id="kiosk-serial"
              v-model="kiosk.version"
              :placeholder="$t('kiosks.version')"
              :disabled="true"
            />
          </div>

        </div>

        <div class="row">

          <div class="col-12 col-md-6">
            <label class="form-label">{{$t('kiosks.serial')}}</label>
            <va-input
              id="kiosk-serial"
              v-model="kiosk.serial"
              :placeholder="$t('kiosks.serial')"
              :disabled="true"
            />
          </div>

          <div class="col-12 col-md-6">
            <label for="kiosk-types-multiselect" class="form-label"> {{$t('kiosks.type')}} </label>
            <multiselect
              id="kiosk-types-multiselect"
              v-model="form.data.kiosk_type"
              :options="kioskTypes"
              track-by="id"
              label="name"
              select-label=""
              deselect-label="×"
              open-direction="bellow"
              placeholder="---"
              @select="form.errors.clear('kiosk_type')"
              :allow-empty="false"
              :multiple="false"
              :disabled="!isFromEvollu"
              >
                <template slot="option" slot-scope="props">
                  {{ $t(props.option.name) }}
                </template>
                <template slot="singleLabel" slot-scope="option">
                  {{ $t(option.option.name) }}
                </template>
            </multiselect>
            <small v-show="form.errors.has('kiosk_type')"  class="help text-danger">
              {{ $t(form.errors.get('kiosk_type')) }}
            </small>
          </div>

        </div>

        <div class="row">

          <div class="col-12 col-md-6">
            <label for="facilities-multiselect" class="form-label">
              {{$t('kiosks.facility')}}
            </label>
            <multiselect
              id="facilities-multiselect"
              v-model="form.data.facility"
              :options="facilities"
              track-by="id"
              label="name"
              select-label=""
              deselect-label="×"
              open-direction="bellow"
              placeholder="---"
              @select="form.errors.clear('facility_id')"
              :allow-empty="false"
              :multiple="false"
              :disabled="!isFromEvollu"
              >
            </multiselect>
            <small v-show="form.errors.has('facility_id')" class="help text-danger">
              {{ $t(form.errors.get('facility_id')) }}
            </small>
          </div>

          <div class="col-12 col-md-6">
            <label class="form-label">{{$t('kiosks.owner')}}</label>
            <va-input
              id="kiosk-owner"
              :value="kiosk.active_license && kiosk.active_license.entity ? kiosk.active_license.entity.name : ''"
              :placeholder="$t('kiosks.owner')"
              :disabled="true"
            />
          </div>
        </div>

        <div class="row">
          <div class="col-12 col-md-6">
            <label class="form-label">{{$t('kiosks.checkin_at')}}</label>
            <va-input
              id="kiosk-checkin-at"
              :value="kiosk.checkin_at"
              :placeholder="$t('kiosks.owner')"
              :disabled="true"
            />
          </div>

          <div class="col-12 col-md-6">
            <label for="facilities-multiselect" class="form-label">
              {{$t('kiosks.calibration')}}
            </label>
            <multiselect
              id="calibrations-multiselect"
              v-model="form.data.calibration"
              :options="calibrationOptions"
              track-by="id"
              label="friendly_name"
              select-label=""
              deselect-label="×"
              open-direction="bellow"
              placeholder="---"
              @select="form.errors.clear('facility_id')"
              :allow-empty="false"
              :multiple="false"
              :disabled="!isFromEvollu"
            >
            </multiselect>
            <small v-show="form.errors.has('facility_id')" class="help text-danger">
              {{ $t(form.errors.get('facility_id')) }}
            </small>
          </div>
        </div>

        <div class="row" v-if="kiosk.calibration">
          <div class="col-12 col-md-6">
            <label class="form-label">{{$t('kiosks.calibration_volume')}}</label>
            <va-input
              id="kiosk-calibration-volume"
              :value="form.data.calibration.volume || 'N/A'"
              :placeholder="$t('kiosks.calibration_volume')"
              :disabled="true"
            />
          </div>

          <div class="col-12 col-md-6">
            <label class="form-label">{{$t('kiosks.calibration_frequencies')}}</label>
            <va-input
              id="kiosk-calibration-frequencies"
              :value="calibrationFrequencies"
              :placeholder="$t('kiosks.calibration_frequencies')"
              :disabled="true"
            />
          </div>
        </div>

        <div class="row">
          <!-- Hexnode server -->
          <div class="col-12 col-md-6">
            <label for="hexnode-servers-multiselect" class="form-label">
              {{$t('kiosks.hexnode_server')}}
            </label>
            <multiselect
            id="hexnode-servers-multiselect"
            v-model="selectedHexnodeServer"
            :options="hexnodeServers"
            track-by="id"
            label="name"
            select-label=""
            deselect-label="×"
            open-direction="bellow"
            placeholder="---"
            :multiple="false"
            :disabled="hexnodeServerSelectDisabled"
            >

            </multiselect>
          </div>

          <!-- Tablet input -->
          <div class="col-12 col-md-6">
            <label for="hexnode-device-multiselect" class="form-label">
              {{$t('kiosks.hexnode_device')}}
            </label>
            <multiselect
              id="hexnode-device-multiselect"
              v-model="selectedHexnodeDevice"
              :options="hexnodeDevices"
              track-by="id"
              label="device-name"
              :custom-label="formatedDeviceLabel"
              select-label=""
              deselect-label="×"
              open-direction="bellow"
              placeholder="---"
              :multiple="false"
              :disabled="!showHexnodeDeviceSelection"
            >
            </multiselect>
          </div>
        </div>

      </div>

    </va-modal>

    <ConfirmationModal
      v-if="isDestroyingKiosk"
      :title="$t('kiosks.delete_kiosk')"
      :body="$t('kiosks.delete_modal_body', {name: kiosk.name})"
      @modal-confirmed="destroyKiosk"
      @modal-canceled="cancelDestroyKiosk" />

  </div>
</template>
<script>
import { KiosksService, EntitiesService } from '@/api/LeadsService'
import Errors from '@/utils/StandardErrors.js'
import ConfirmationModal from '@/components/common/ConfirmationModal'
import LoadingAnimation from '../../users/components/Helpers/LoadingAnimation'
import Multiselect from 'vue-multiselect'
import IsFromEvolluMixin from '@/mixins/IsFromEvolluMixin'

const EntitiesInfoType = 110
const ReadAccessType = 1
export default {
  props: {
    kiosk: {
      type: Object,
      required: true
    },
  },
  mixins: [
    IsFromEvolluMixin
  ],
  components: {
    ConfirmationModal,
    Multiselect,
    LoadingAnimation
  },
  created: function () {

  },
  mounted: function () {
    this.showKioskModal = true
    this.getFacilities()
      .then(facilities => {
        this.facilities = facilities
        this.getCalibrations()
          .then(calibs => {
            this.calibrationOptions = calibs
            if (this.isUpdate) {
              this.prepareForUpdate()
            }
          })
      })
      .finally(() => {
        this.isLoading = false
      })
  },
  data: function () {
    return {
      isLoading: true,
      showKioskModal: false,
      isDestroyingKiosk: false,
      calibrationOptions: [],
      facilities: [],
      selectedHexnodeServer: null,
      hexnodeServerSelectDisabled: false,
      hexnodeDevices: [],
      showHexnodeDeviceSelection: false,
      selectedHexnodeDevice: null,
      submitDisabled: false,
      shouldSetHexnodeDevicesBasedOnSavedData: false,
      form: {
        data: {
          name: null,
          facility: null,
          kiosk_type: null,
          calibration: '',
        },
        errors: new Errors()

      },
      kioskTypes: [
        {
          id: 1,
          name: 'kiosks.regular_kiosk'
        },
        {
          id: 2,
          name: 'kiosks.portable_kiosk'
        }
      ],
      hexnodeServers: [
        {
          id: 1,
          name: "Evollu",
          slug: "evollu",
        },
        {
          id: 2,
          name: "Evollu 1",
          slug: "evollu1",
        }
      ]
    }
  },
  computed: {
    isUpdate: function () {
      return this.kiosk !== undefined
    },
    leadsService: function () {
      return this.$store.state.services.find(s => s.url === this.$store.state.evollead_server)
    },
    hasWritePermissions: function () {
      return this.leadsService.user_permissions.some(p => p.info_type === 110 && p.access_type >= 2 && (p.entity_id === null || p.entity_id === this.kiosk.entity.id))
    },
    calibrationFrequencies: function () {
      if (this.form.data && this.form.data.calibration && this.form.data.calibration.frequencies) {
        return this.form.data.calibration.frequencies.map(f => f.freq).join(', ')
      }
      return 'N/A'
    }
  },
  methods: {
    formatedDeviceLabel: function (device) {
      return `${device.device_name} (${device.serial_number})`
    },
    submit: function () {
      let data = this.getFormData()
      let request = this.isUpdate ? KiosksService.update(this.kiosk.id, data) : KiosksService.store(data)
      request
        .then(kiosk => {
          this.$refs.createOrUpdateKiosk.cancel()
          this.$emit(this.isUpdate ? 'updated' : 'created', kiosk)
          this.showToast(this.isUpdate ? this.$t('kiosks.kiosk_updated') : this.$t('kiosks.kiosk_created'), this.$store.state.successToastOptions)
        })
        .catch(errors => {
          if (errors.response.status === 422) {
            this.form.errors.record(errors.response.data.errors, 'kiosks.errors.')
            this.showToast(this.$t('kiosks.form_contains_errors'), this.$store.state.errorToastOptions)
          } else if (errors.response.status === 403) {
            this.showToast(this.$t('kiosks.missing_permissions'), this.$store.state.errorToastOptions)
          } else {
            this.showToast(this.$t('kiosks.something_went_wrong'), this.$store.state.errorToastOptions)
          }
        })
    },
    getFormData: function () {
      return {
        name: this.form.data.name,
        serial: this.form.data.serial,
        kiosk_type: this.form.data.kiosk_type ? this.form.data.kiosk_type.id : null,
        facility_id: this.form.data.facility ? this.form.data.facility.id : null,
        calibration: this.form.data.calibration || null,
        hexnode_server_slug: this.selectedHexnodeServer ? this.selectedHexnodeServer.slug : null,
        hexnode_device_id: this.selectedHexnodeDevice ? this.selectedHexnodeDevice.id : null,
        hexnode_device_serial: this.selectedHexnodeDevice ? this.selectedHexnodeDevice.serial_number : null
      }
    },
    close: function () {
      if (!this.isDestroyingKiosk) {
        setTimeout(() => {
          this.$emit('modalClosed')
        }, 450)
      }
    },
    prepareForUpdate: function () {
      this.form.data = JSON.parse(JSON.stringify(this.kiosk))

      if (this.kiosk.kiosk_type) {
        this.form.data.kiosk_type = this.kioskTypes.find(kioskType => kioskType.id === this.kiosk.kiosk_type) || null
      }

      if (this.kiosk.facility) {
        this.form.data.facility = this.facilities.find(facility => this.kiosk.facility.id === facility.id) || null
      }

      if (this.kiosk.calibration) {
        this.form.data.calibration = this.calibrationOptions.find(option => this.kiosk.calibration.id === option.id)
      }

      if (this.kiosk.hexnode_server_slug) {
        this.shouldSetHexnodeDevicesBasedOnSavedData = true
        this.selectedHexnodeServer = this.hexnodeServers.find(serv => this.kiosk.hexnode_server_slug === serv.slug)
      }
    },
    prepareForDestroy: function () {
      this.isDestroyingKiosk = true
      this.showKioskModal = false
    },
    cancelDestroyKiosk: function () {
      this.isDestroyingKiosk = false
      this.showKioskModal = true
    },
    destroyKiosk: function () {
      this.isDestroyingKiosk = false
      this.showKioskModal = true
    },
    getCalibrations: function () {
      return new Promise((resolve, reject) => {
        KiosksService.getCalibrations().then(response => {
          resolve(response)
        }).catch(errors => {
          console.log(errors)
          this.showToast(this.$t('kiosks.something_went_wrong'), this.$store.state.errorToastOptions)
          resolve([])
        })
      })
    },
    getFacilities: function () {
      return new Promise((resolve, reject) => {
        EntitiesService.query({
          include: 'facilities',
          access: ReadAccessType,
          info: EntitiesInfoType
        })
          .then(response => {
            let facilities = []
            const entities = response.data.data
            if (entities.length) {
              facilities = entities.map(entity => entity.facilities).flat()
            }
            resolve(facilities)
          })
          .catch(errors => {
            this.showToast(this.$t('kiosks.something_went_wrong'), this.$store.state.errorToastOptions)
            resolve([])
          })
      })
    }
  }, watch: {
    selectedHexnodeServer: function (value) {
      if(value) {
        this.showHexnodeDeviceSelection = false
        this.hexnodeDevices = []
        this.selectedHexnodeDevice = null
        this.hexnodeServerSelectDisabled = true
        this.submitDisabled = true
        KiosksService.getAllHexnodeDevices({'hexnode_service_slug': this.selectedHexnodeServer.slug})
        .then(res => {
          this.hexnodeDevices = res
          this.showHexnodeDeviceSelection = true
          if(this.shouldSetHexnodeDevicesBasedOnSavedData) {
            if (this.kiosk.hexnode_device_id) {
              this.selectedHexnodeDevice = this.hexnodeDevices.find(device => parseInt(this.kiosk.hexnode_device_id) === device.id)
            }
            this.shouldSetHexnodeDevicesBasedOnSavedData = false
          }
        }). catch(err => {
          this.showToast(this.$t('kiosks.hexnode_devices.something_went_wrong'), this.$store.state.errorToastOptions)
        })
        .finally(() => {
          this.submitDisabled = false
          this.hexnodeServerSelectDisabled = false
        })
      }
    },
  }
}
</script>
